import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import styles from "../../assets/styles/AboutUs/AboutUs.module.css";
import AboutImg from "../../assets/images/AboutUs.webp";
import AboutImg1 from "../../assets/images/aboutyoga.jpg";
import { ABOUT_US_DEFAULTS, ABOUT_US_EMPTY_STATE } from "../../data/aboutUsDefaults";
import GoogleReviews from "../Homepage/GoogleReviews";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const ABOUT_US_API = `${API_BASE}/api/about-us`;
const TEACHERS_API = `${API_BASE}/api/about-teacher`;

const resolveAssetUrl = (value, fallback = "") => {
  if (!value) return fallback;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/uploads/")) return `${API_BASE}${value}`;
  if (value.startsWith("uploads/")) return `${API_BASE}/${value}`;
  return `${API_BASE}/uploads/${value}`;
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const normalizeAboutUs = (item) => {
  const defaults = ABOUT_US_EMPTY_STATE();
  const data = item || {};

  return {
    hero: { ...defaults.hero, ...(data.hero || {}) },
    stats: data.stats?.length > 0 ? data.stats : defaults.stats,
    floatingCards:
      data.floatingCards?.length > 0 ? data.floatingCards : defaults.floatingCards,
    overview: {
      ...defaults.overview,
      ...(data.overview || {}),
      paragraphs:
        data.overview?.paragraphs?.length > 0
          ? data.overview.paragraphs
          : defaults.overview.paragraphs,
      highlights:
        data.overview?.highlights?.length > 0
          ? data.overview.highlights
          : defaults.overview.highlights,
    },
    missionVision: {
      ...defaults.missionVision,
      ...(data.missionVision || {}),
      missionPoints:
        data.missionVision?.missionPoints?.length > 0
          ? data.missionVision.missionPoints
          : defaults.missionVision.missionPoints,
      visionPoints:
        data.missionVision?.visionPoints?.length > 0
          ? data.missionVision.visionPoints
          : defaults.missionVision.visionPoints,
    },
    values: {
      ...defaults.values,
      ...(data.values || {}),
      items: data.values?.items?.length > 0 ? data.values.items : defaults.values.items,
    },
    timeline: {
      ...defaults.timeline,
      ...(data.timeline || {}),
      items:
        data.timeline?.items?.length > 0 ? data.timeline.items : defaults.timeline.items,
    },
    whyChoose: {
      ...defaults.whyChoose,
      ...(data.whyChoose || {}),
      items:
        data.whyChoose?.items?.length > 0
          ? data.whyChoose.items
          : defaults.whyChoose.items,
    },
    goals: {
      ...defaults.goals,
      ...(data.goals || {}),
      items: data.goals?.items?.length > 0 ? data.goals.items : defaults.goals.items,
    },
    facilities: {
      ...defaults.facilities,
      ...(data.facilities || {}),
      items:
        data.facilities?.items?.length > 0
          ? data.facilities.items
          : defaults.facilities.items,
    },
    studentReviews: {
      ...defaults.studentReviews,
      ...(data.studentReviews || {}),
    },
    cta: {
      ...defaults.cta,
      ...(data.cta || {}),
    },
  };
};

const normalizeTeacherRecord = (item) => {
  const teachers = item?.teacherSection?.teachers || [];
  return {
    sectionLabel: item?.teacherSection?.teacherHeading || "Meet Our Masters",
    sectionTitle: item?.teacherSection?.teacherMainHeading || "Our Expert Teachers",
    sectionDesc:
      item?.teacherSection?.teacherMainPara ||
      "Learn from experienced yoga masters dedicated to your transformation",
    teachers: teachers.map((teacher) => ({
      teacherName: teacher.teacherName || "",
      role: teacher.role || "",
      description: teacher.description || "",
      experience: teacher.experience?.[0] || "",
      specialty: teacher.expertise?.[0] || "",
      teacherImage: teacher.teacherImage || "",
    })),
  };
};

export default function AboutUs() {
  const [aboutData, setAboutData] = useState(() => clone(ABOUT_US_DEFAULTS));
  const [teacherData, setTeacherData] = useState(() => normalizeTeacherRecord(null));
  const [activeYear, setActiveYear] = useState(
    ABOUT_US_DEFAULTS.timeline.items.at(-1)?.year || 2024
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const fetchData = async () => {
      setLoading(true);

      try {
        const [aboutRes, teacherRes] = await Promise.all([
          axios.get(ABOUT_US_API).catch(() => ({ data: null })),
          axios.get(TEACHERS_API).catch(() => ({ data: null })),
        ]);

        if (!active) return;

        const aboutPayload = Array.isArray(aboutRes.data?.data)
          ? aboutRes.data.data[0]
          : Array.isArray(aboutRes.data)
          ? aboutRes.data[0]
          : aboutRes.data?.data || aboutRes.data || null;

        const teacherPayload = Array.isArray(teacherRes.data?.data)
          ? teacherRes.data.data[0]
          : Array.isArray(teacherRes.data)
          ? teacherRes.data[0]
          : teacherRes.data?.data || teacherRes.data || null;

        const normalizedAbout = normalizeAboutUs(aboutPayload);
        setAboutData(normalizedAbout);
        setTeacherData(normalizeTeacherRecord(teacherPayload));

        const latestYear =
          normalizedAbout.timeline.items.at(-1)?.year ||
          ABOUT_US_DEFAULTS.timeline.items.at(-1)?.year ||
          2024;
        setActiveYear(latestYear);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, []);

  const activeTimelineYear = useMemo(() => {
    const availableYears = aboutData.timeline.items.map((item) => item.year);
    return availableYears.includes(activeYear)
      ? activeYear
      : aboutData.timeline.items.at(-1)?.year;
  }, [aboutData.timeline.items, activeYear]);

  return (
    <div className={styles.container}>
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
          <img
            src={resolveAssetUrl(aboutData.hero.heroImage, AboutImg)}
            alt="Hatha Yogashram"
          />
        </div>
        <div className={styles.heroContent}>
          <p className={styles.quoteText}>{aboutData.hero.quoteText}</p>
          <h1 className={styles.mainTitle}>{aboutData.hero.title}</h1>
          <div className={styles.breadcrumb}>
            <span className={styles.breadcrumbLink}>Home</span>
            <span className={styles.breadcrumbSeparator}>/</span>
            <span>About Us</span>
          </div>
        </div>
      </section>

      <section className={styles.heroStats}>
        <div className={styles.statsContainer}>
          {aboutData.stats.map((stat, index) => (
            <div className={styles.stat} key={`stat-${index}`}>
              <div className={styles.statIcon}>{stat.icon}</div>
              <div className={styles.statValue}>{stat.value}</div>
              <div className={styles.statLabel}>{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.floatingSection}>
        <div className={styles.container}>
          <div className={styles.floatingCards}>
            {aboutData.floatingCards.map((card, index) => (
              <div className={styles.floatCard} key={`float-${index}`}>
                <div className={styles.cardIcon}>{card.icon}</div>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.overviewSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{aboutData.overview.sectionLabel}</span>
            <h2 className={styles.sectionTitle}>{aboutData.overview.sectionTitle}</h2>
            <div className={styles.decorativeLine}></div>
          </div>
          <div className={styles.overviewGrid}>
            <div className={styles.overviewContent}>
              <p className={styles.leadText}>{aboutData.overview.leadText}</p>
              {aboutData.overview.paragraphs.map((paragraph, index) => (
                <p className={styles.bodyText} key={`overview-paragraph-${index}`}>
                  {paragraph}
                </p>
              ))}
              <div className={styles.highlights}>
                {aboutData.overview.highlights.map((highlight, index) => (
                  <div className={styles.highlight} key={`highlight-${index}`}>
                    <div className={styles.highlightIcon}>{highlight.icon}</div>
                    <div>
                      <h4>{highlight.title}</h4>
                      <p>{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.overviewImage}>
              <img
                src={resolveAssetUrl(aboutData.overview.aboutImage, AboutImg1)}
                alt={aboutData.overview.sectionTitle}
              />
              <div className={styles.imageOverlay}>
                <div className={styles.mantraBox}>
                  <div className={styles.mantraSymbol}>
                    {aboutData.overview.mantraSymbol}
                  </div>
                  <p>{aboutData.overview.mantraText}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.missionSection}>
        <div className={styles.container}>
          <div className={styles.missionGrid}>
            <div className={styles.missionCard}>
              <div className={styles.missionIcon}>
                <div className={styles.iconCircle}>🎯</div>
              </div>
              <h3>{aboutData.missionVision.missionTitle}</h3>
              <p>{aboutData.missionVision.missionDescription}</p>
              <ul>
                {aboutData.missionVision.missionPoints.map((point, index) => (
                  <li key={`mission-point-${index}`}>{point}</li>
                ))}
              </ul>
            </div>

            <div className={styles.visionCard}>
              <div className={styles.visionIcon}>
                <div className={styles.iconCircle}>👁️</div>
              </div>
              <h3>{aboutData.missionVision.visionTitle}</h3>
              <p>{aboutData.missionVision.visionDescription}</p>
              <ul>
                {aboutData.missionVision.visionPoints.map((point, index) => (
                  <li key={`vision-point-${index}`}>{point}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.valuesSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{aboutData.values.sectionLabel}</span>
            <h2 className={styles.sectionTitle}>{aboutData.values.sectionTitle}</h2>
            <div className={styles.decorativeLine}></div>
            <p className={styles.sectionDesc}>{aboutData.values.sectionDesc}</p>
          </div>
          <div className={styles.valuesGrid}>
            {aboutData.values.items.map((value, index) => (
              <div key={`value-${index}`} className={styles.valueCard}>
                <div className={styles.valueIcon}>{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.timelineSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{aboutData.timeline.sectionLabel}</span>
            <h2 className={styles.sectionTitle}>{aboutData.timeline.sectionTitle}</h2>
            <div className={styles.decorativeLine}></div>
            <p className={styles.sectionDesc}>{aboutData.timeline.sectionDesc}</p>
          </div>
          <div className={styles.timeline}>
            {aboutData.timeline.items.map((milestone, index) => (
              <div
                key={`milestone-${index}`}
                className={`${styles.timelineItem} ${
                  activeTimelineYear === milestone.year ? styles.timelineItemActive : ""
                }`}
                onClick={() => setActiveYear(milestone.year)}
              >
                <div className={styles.timelineYear}>{milestone.year}</div>
                <div className={styles.timelineContent}>
                  <h4>{milestone.title}</h4>
                  <p>{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.teachersSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{teacherData.sectionLabel}</span>
            <h2 className={styles.sectionTitle}>{teacherData.sectionTitle}</h2>
            <div className={styles.decorativeLine}></div>
            <p className={styles.sectionDesc}>{teacherData.sectionDesc}</p>
          </div>
          <div className={styles.teachersGrid}>
            {teacherData.teachers.map((teacher, index) => (
              <div key={`teacher-${index}`} className={styles.teacherCard}>
                <div className={styles.teacherImage}>
                  {teacher.teacherImage ? (
                    <img
                      src={resolveAssetUrl(teacher.teacherImage)}
                      alt={teacher.teacherName}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "inherit",
                        display: "block",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "3rem",
                        background: "#f5ede8",
                      }}
                    >
                      🧘
                    </div>
                  )}
                </div>
                <div className={styles.teacherInfo}>
                  <h3>{teacher.teacherName}</h3>
                  <p className={styles.teacherRole}>{teacher.role}</p>
                  <div className={styles.teacherMeta}>
                    {teacher.experience && (
                      <span className={styles.experience}>📚 {teacher.experience}</span>
                    )}
                    {teacher.specialty && (
                      <span className={styles.specialty}>⭐ {teacher.specialty}</span>
                    )}
                  </div>
                  <p className={styles.teacherDesc}>{teacher.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.whyChooseSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{aboutData.whyChoose.sectionLabel}</span>
            <h2 className={styles.sectionTitle}>{aboutData.whyChoose.sectionTitle}</h2>
            <div className={styles.decorativeLine}></div>
          </div>
          <div className={styles.reasonsGrid}>
            {aboutData.whyChoose.items.map((item, index) => (
              <div key={`reason-${index}`} className={styles.reasonCard}>
                <div className={styles.reasonNumber}>{item.number}</div>
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.goalsSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{aboutData.goals.sectionLabel}</span>
            <h2 className={styles.sectionTitle}>{aboutData.goals.sectionTitle}</h2>
            <div className={styles.decorativeLine}></div>
          </div>
          <div className={styles.goalsGrid}>
            {aboutData.goals.items.map((item, index) => (
              <div key={`goal-${index}`} className={styles.goalCard}>
                <div className={styles.goalIcon}>{item.icon}</div>
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.facilitiesSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>
              {aboutData.facilities.sectionLabel}
            </span>
            <h2 className={styles.sectionTitle}>{aboutData.facilities.sectionTitle}</h2>
            <div className={styles.decorativeLine}></div>
          </div>
          <div className={styles.facilitiesGrid}>
            {aboutData.facilities.items.map((item, index) => (
              <div key={`facility-${index}`} className={styles.facilityCard}>
                <div className={styles.facilityIcon}>{item.icon}</div>
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GoogleReviews
        sectionLabel={aboutData.studentReviews.sectionLabel}
        heading={aboutData.studentReviews.sectionTitle}
        description={aboutData.studentReviews.sectionDesc}
      />

      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2>{aboutData.cta.title}</h2>
          <p>{aboutData.cta.description}</p>
          <div className={styles.ctaButtons}>
            <button className={styles.ctaPrimary}>
              {aboutData.cta.primaryButtonText}
            </button>
            <button className={styles.ctaSecondary}>
              {aboutData.cta.secondaryButtonText}
            </button>
          </div>
        </div>
      </section>

      {loading && (
        <div
          style={{
            position: "fixed",
            bottom: 16,
            right: 16,
            background: "#ffffff",
            border: "1px solid #eee",
            padding: "10px 14px",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            zIndex: 20,
          }}
        >
          Refreshing About Us content...
        </div>
      )}
    </div>
  );
}
